import { useEffect, useRef } from 'react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';

type FullScreenLayoutProps = {
    /** Shown in the top bar; the page supplies it already translated. */
    title: string;
    /**
     * Optional link back into the authenticated admin app. Visitors without a
     * session are caught by `AuthGuard` on the target route, so the button can
     * be shown unconditionally.
     */
    backLink?: {
        to: string;
        /** Already translated by the page. */
        label: string;
    };
};

/**
 * CSS custom property holding the measured height of the sticky header. Pages
 * use it to offset their own sticky elements so they pin below the bar instead
 * of sliding underneath it — the bar's height is content-driven (title, buttons)
 * and would otherwise have to be hardcoded.
 */
export const APP_BAR_HEIGHT_VAR = '--full-screen-app-bar-height';

/**
 * Chrome-light layout for standalone pages that are reached by URL rather than
 * through the admin navigation. Unlike `SidebarLayout` it has no sidebar, no
 * account popover and no analytics injection — those are all signed-in admin
 * concerns, and pages using this layout may be unauthenticated.
 *
 * `minHeight` rather than `height` keeps scrolling at the document level: the
 * page simply grows past the viewport instead of introducing nested scroll
 * containers.
 */
export const FullScreenLayout: FunctionComponent<
    PropsWithChildren<FullScreenLayoutProps>
> = ({ children, title, backLink }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    // Publish the header height as a CSS variable rather than a constant, so it
    // stays correct when the bar reflows (long titles, wrapped buttons).
    useEffect(() => {
        const root = rootRef.current;
        const header = headerRef.current;
        if (!root || !header) return;

        const sync = () =>
            root.style.setProperty(
                APP_BAR_HEIGHT_VAR,
                `${header.getBoundingClientRect().height}px`
            );

        sync();
        const observer = new ResizeObserver(sync);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={rootRef}
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                component='header'
                ref={headerRef}
                sx={(theme) => ({
                    position: 'sticky',
                    top: 0,
                    zIndex: theme.zIndex.appBar,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 4,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                })}
            >
                <Box
                    component='img'
                    src='/favicon/favicon-32x32.png'
                    alt='Starjack Logo'
                    sx={{ width: 32, height: 32 }}
                />
                <Typography variant='h6'>{title}</Typography>
                <Box
                    sx={{
                        ml: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {backLink && (
                        <Button
                            component={RouterLink}
                            to={backLink.to}
                            size='small'
                            color='inherit'
                            startIcon={<ArrowBackIcon />}
                        >
                            {backLink.label}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box component='main' sx={{ flex: 1, px: 4, py: 4 }}>
                {children}
            </Box>
        </Box>
    );
};
